/**
 * PHASE 1 OPTIMIZATION: Shared Gmail Accounts Hook
 * 
 * This hook consolidates all /api/accounts-v2/current calls into a single
 * React Query cached instance, eliminating duplicate API calls and Firestore reads.
 * 
 * Before: CreateCampaign.js and ManageAccounts.js each called the endpoint independently
 * After: Both components share the same cached data
 * 
 * Expected Impact: 50% reduction in /api/accounts-v2/current API calls
 */

import { useQuery } from '@tanstack/react-query';
import { getAuth } from 'firebase/auth';
import axiosAuth from '../utils/axiosAuth';

/**
 * Fetch Gmail accounts for the current user
 * @param {string} userId - Firebase UID
 * @param {object} options - React Query options
 * @returns {object} React Query result with accounts data
 */
export const useGmailAccounts = (userId, options = {}) => {
  return useQuery({
    queryKey: ['gmail-accounts', userId],
    
    queryFn: async () => {
      console.log('🔍 [useGmailAccounts] Fetching Gmail accounts from API');
      
      // Ensure fresh token
      const user = getAuth().currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const idToken = await user.getIdToken();
      localStorage.setItem('authToken', idToken);

      // Make API call
      const response = await axiosAuth.get('/api/accounts-v2/current');
      
      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to fetch Gmail accounts');
      }

      const accounts = response.data.accounts || [];
      console.log(`✅ [useGmailAccounts] Loaded ${accounts.length} Gmail accounts from API`);
      
      return {
        mainEmail: response.data.email,
        accounts: accounts,
        totalQuota: accounts.reduce((sum, account) => sum + (account.quotaRemaining || 0), 0),
        capabilities: {
          advancedCampaignSending: response.data.capabilities?.advancedCampaignSending === true
        }
      };
    },
    
    // Only run if user is authenticated and enabled flag is true
    enabled: !!userId && (options.enabled !== false),
    
    // Cache settings: balance freshness with performance
    staleTime: 5 * 60 * 1000,           // 5 minutes - data considered fresh
    cacheTime: 10 * 60 * 1000,          // 10 minutes - keep in memory
    
    // Always show cached data first for instant UI
    keepPreviousData: true,
    
    // Refetch strategies
    refetchOnMount: 'always',           // Check for updates on mount
    refetchOnWindowFocus: true,         // Refetch when window gains focus
    refetchOnReconnect: true,           // Refetch when network reconnects
    
    // Logging
    onSuccess: (data) => {
      console.log('✅ [useGmailAccounts] Shared hook loaded from cache', {
        accounts: data.accounts.length,
        totalQuota: data.totalQuota
      });
    },
    
    onError: (error) => {
      console.error('❌ [useGmailAccounts] Error fetching accounts', { status: error?.response?.status || null });
    },
    
    // Allow options override
    ...options
  });
};

/**
 * Hook to invalidate Gmail accounts cache
 * Use this after adding/removing accounts or updating quota
 */
export const useInvalidateGmailAccounts = () => {
  const queryClient = require('@tanstack/react-query').useQueryClient();
  
  return (userId) => {
    queryClient.invalidateQueries(['gmail-accounts', userId]);
    console.log('🔄 [useGmailAccounts] Authenticated account cache invalidated');
  };
};

// TODO Phase 2: Consider adding Firestore listener for real-time quota updates
// TODO Phase 2: Add optimistic updates when removing accounts
